import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import LoginPageCardDialog from './loginPageCardDialog';
import {
    deleteLoginPageCard,
    listLoginPageCards,
    listPersonaOptions,
    updateLoginPageCard,
} from './loginPageCards.service';
import { CARD_KINDS, toRequest } from './loginPageCards.constants';

const dateWindow = (card) => {
    if (card.startsOn && card.endsOn) return `${card.startsOn} to ${card.endsOn}`;
    if (card.startsOn) return `From ${card.startsOn}`;
    if (card.endsOn) return `Until ${card.endsOn}`;
    return 'Always';
};

const byOrder = (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title);

/**
 * The cards on the eK Link page shown after sign-in, next to the apps the user can open: eKutir news,
 * other services and Initiatives, and recommendations. Each environment keeps its own cards.
 */
function LoginPageCards() {
    const [cards, setCards] = useState(null);
    const [personas, setPersonas] = useState([]);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [busy, setBusy] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = useCallback((message, severity) => setSnackbar({ open: true, message, severity }), []);
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    useEffect(() => {
        let active = true;
        listLoginPageCards()
            .then((list) => active && setCards(list))
            .catch((e) => active && setError(e.message));
        listPersonaOptions()
            .then((list) => active && setPersonas(list))
            .catch((e) => active && notify(e.message, 'error'));
        return () => {
            active = false;
        };
    }, [notify]);

    const replace = (saved) =>
        setCards((current) => (current.some((c) => c.id === saved.id)
            ? current.map((c) => (c.id === saved.id ? saved : c))
            : [...current, saved]));

    const toggleActive = async (card) => {
        try {
            replace(await updateLoginPageCard(card.id, { ...toRequest(card), active: !card.active }));
        } catch (e) {
            notify(e.message, 'error');
        }
    };

    const confirmDelete = async () => {
        setBusy(true);
        try {
            await deleteLoginPageCard(deleting.id);
            setCards((current) => current.filter((c) => c.id !== deleting.id));
            notify('Card deleted', 'success');
            setDeleting(null);
        } catch (e) {
            notify(e.message, 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' }, mb: 3 }}>
                <Box>
                    <Typography className="app-page-title" sx={{ fontSize: 30, fontWeight: 700, color: '#202124', mb: 1 }}>
                        eK Link Login Page
                    </Typography>
                    <Typography color="text.secondary">
                        These cards show on the eK Link page after sign-in, next to the apps the user can open.
                        A card without personas shows to everyone. These settings apply to this environment only.
                    </Typography>
                </Box>
                <Button variant="contained" onClick={() => setEditing({})} sx={{ flexShrink: 0 }}>Add card</Button>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {!cards && !error ? <CircularProgress /> : null}
            {cards ? (
                <Stack spacing={4}>
                    {CARD_KINDS.map((kind) => {
                        const ofKind = cards.filter((c) => c.kind === kind.value).sort(byOrder);
                        return (
                            <Box key={kind.value}>
                                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{kind.label}</Typography>
                                <Typography color="text.secondary" sx={{ mb: 1.5 }}>{kind.hint}</Typography>
                                {ofKind.length === 0 ? (
                                    <Typography color="text.secondary">No cards yet.</Typography>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {ofKind.map((card) => (
                                            <Paper key={card.id} variant="outlined" sx={{ p: 2, borderRadius: 2, opacity: card.active ? 1 : 0.6 }}>
                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ justifyContent: 'space-between' }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontWeight: 700 }}>{card.title}</Typography>
                                                        {card.body ? (
                                                            <Typography color="text.secondary" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                                {card.body}
                                                            </Typography>
                                                        ) : null}
                                                        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}>
                                                            {card.personas?.length ? (
                                                                card.personas.map((p) => <Chip key={p} size="small" label={p} />)
                                                            ) : (
                                                                <Chip size="small" label="Everyone" />
                                                            )}
                                                            <Typography variant="caption" color="text.secondary">
                                                                {dateWindow(card)} · order {card.sortOrder}
                                                                {card.hideIfAppUrl ? ` · hidden for users of ${card.hideIfAppUrl}` : ''}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={card.active}
                                                                    onChange={() => toggleActive(card)}
                                                                    inputProps={{ 'aria-label': `Show ${card.title}` }}
                                                                />
                                                            }
                                                            label="Active"
                                                        />
                                                        <Button onClick={() => setEditing(card)}>Edit</Button>
                                                        <Button color="error" onClick={() => setDeleting(card)}>Delete</Button>
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            ) : null}

            {editing ? (
                <LoginPageCardDialog
                    card={editing}
                    personas={personas}
                    onClose={() => setEditing(null)}
                    onSaved={(saved) => {
                        replace(saved);
                        setEditing(null);
                        notify('Card saved', 'success');
                    }}
                />
            ) : null}

            <Dialog open={Boolean(deleting)} onClose={() => (busy ? undefined : setDeleting(null))} fullWidth maxWidth="xs">
                <DialogTitle>Delete this card?</DialogTitle>
                <DialogContent>
                    <Typography>
                        “{deleting?.title}” disappears from the eK Link page for everyone. To hide it for a while, switch it off instead.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setDeleting(null)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}

export default LoginPageCards;
